// client/src/app/login/page.js
'use client';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faChalkboardTeacher,
    faChevronRight,
    faEye,
    faEyeSlash,
    faHandshake,
    faSchool,
    faUserGraduate,
    faXmark,
} from '@fortawesome/free-solid-svg-icons';
import React, { useState, useEffect, Suspense, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import Image from 'next/image';
import Script from 'next/script';

import Link from 'next/link';

import { API_URL, GOOGLE_CLIENT_ID } from '@/variables';
import { useTenant } from '@/context/TenantContext';
import { updateSchool } from '@/store/slices/schoolSlice';
import { setTeacher } from '@/store/slices/TeacherSlice';
import { setStudentAuth } from '@/store/slices/StudentAuthSlice';
import { clearLocalSession } from '@/lib/session';
import logo from '../../../public/images/logo.png';
import '../../styles/utils.scss';
import './role-login.scss';

// Google sign-in only works with a real OAuth client (GOOGLE_CLIENT_ID, see variables.js).
// Without one the button would render against a placeholder ID and fail for everyone,
// so the whole option is left out instead.

// Role configurations to drive the dynamic UI
const ROLE_CONFIGS = {
  admin: {
    pill: 'School Admin',
    title: 'Run Your School in One Place',
    lead: 'Access admissions, fees, attendance, exams, and staff operations from your admin command center.',
    highlightTitle: 'Admin Portal Highlights',
    highlightLead: 'Built for principals and office teams to manage every academic and operational workflow without switching tools.',
    highlights: [
      'Class and section wise student operations',
      'Fee dues, collections, and transaction reports',
      'Attendance, exams, and marksheet workflows',
      'Staff, website, enquiries, and inventory modules'
    ],
    apiPath: '/app/admin/login',
    storageRole: 'admin',
    dispatchAction: (data) => updateSchool(data.school)
  },
  teacher: {
    pill: 'Teacher',
    title: 'Welcome Back, Teacher',
    lead: 'Take attendance, publish homework, and track class performance with a focused teaching workspace.',
    highlightTitle: 'Teacher Tools',
    highlightLead: 'Designed for fast daily execution inside classrooms and staff rooms.',
    highlights: [
      'Class-wise attendance in a few taps',
      'Homework publishing and tracking',
      'Notification and notice visibility',
      'Student profile and result context'
    ],
    apiPath: '/app/teacher/login',
    storageRole: 'teacher',
    dispatchAction: (data) => setTeacher({
      teacher: {
        ...data.teacher,
        schoolInfo: data.school
      }
    })
  },
  student: {
    pill: 'Student',
    title: 'Your Learning Dashboard',
    lead: 'Check homework, attendance, fees, and exam results in one simple student portal.',
    highlightTitle: 'Student Quick Access',
    highlightLead: 'Everything students need for daily academic progress and communication.',
    highlights: [
      'Homework and notices timeline',
      'Attendance insights by month',
      'Exam results and marksheets',
      'Fee summary and payment records'
    ],
    apiPath: '/app/student/login',
    storageRole: 'student',
    dispatchAction: (data) => setStudentAuth({
      student: {
        ...data.student,
        schoolInfo: data.school
      }
    })
  }
};

// Cards on the first step of sign-in, where the visitor says who they are.
const ROLE_CHOICES = [
  { role: 'admin', icon: faSchool, label: 'School Admin', tab: 'School Admin', hint: 'Principal, owner or office team' },
  { role: 'teacher', icon: faChalkboardTeacher, label: 'Teacher', tab: 'Teacher', hint: 'Attendance, homework and classes' },
  { role: 'student', icon: faUserGraduate, label: 'Student / Parent', tab: 'Student', hint: 'Results, fees and notices' },
];

// Remembers which door this browser used last, so returning visitors skip the chooser.
const LAST_ROLE_KEY = 'lastLoginRole';
const readLastRole = () => {
  try {
    const stored = localStorage.getItem(LAST_ROLE_KEY);
    return ROLE_CONFIGS[stored] ? stored : null;
  } catch {
    return null; // private mode / blocked storage - just show the chooser
  }
};
const rememberRole = (role) => {
  try { localStorage.setItem(LAST_ROLE_KEY, role); } catch { /* storage unavailable */ }
};

function LoginContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const dispatch = useDispatch();
  const { tenant } = useTenant();
  const googleEnabled = tenant.isPlatformDefault && Boolean(GOOGLE_CLIENT_ID);

  const [activeRole, setActiveRole] = useState('admin');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [submittingInfo, setSubmittingInfo] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  // null until we know whether this visitor needs the chooser (avoids flashing the wrong screen),
  // then 'choose' (who are you?) or 'form' (the sign-in form).
  const [step, setStep] = useState(null);
  const usernameRef = useRef(null);
  const passwordRef = useRef(null);

  // Organic Google Login & Onboarding States
  const [googleLoading, setGoogleLoading] = useState(false);
  const [onboardingModal, setOnboardingModal] = useState(false);
  const [onboardingToken, setOnboardingToken] = useState(null);
  const [googleProfile, setGoogleProfile] = useState(null);
  const [onboardingIntent, setOnboardingIntent] = useState('create_school');
  const [onboardForm, setOnboardForm] = useState({
    schoolName: '',
    schoolAlias: '',
    contactNo: '',
    address: '',
    studentUsername: '',
    studentPassword: '',
    mobilenumber: '',
    organizationName: ''
  });
  const [onboardSubmitting, setOnboardSubmitting] = useState(false);

  // Sync role from query parameter
  useEffect(() => {
    const roleParam = searchParams.get('role');
    if (roleParam && ROLE_CONFIGS[roleParam]) {
      setActiveRole(roleParam);
    }
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get('reason') === 'expired') {
      setNotice('Your session has expired. Please sign in again to continue.');
    }
  }, [searchParams]);

  // Decide the first screen once: a role in the link or remembered from last time goes straight
  // to the form; a brand-new visitor is asked who they are first.
  useEffect(() => {
    const roleParam = searchParams.get('role');
    if (roleParam && ROLE_CONFIGS[roleParam]) {
      setStep('form');
      return;
    }
    // "Continue with Google" on the landing page: Google works out the role itself (admin, teacher,
    // student, or a new school), so there is no point asking who they are first.
    if (searchParams.get('method') === 'google') {
      setStep('form');
      return;
    }
    const last = readLastRole();
    if (last) {
      setActiveRole(last);
      setStep('form');
    } else {
      setStep('choose');
    }
    // Only on first load: later URL changes are the user's own role switches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tab title tells you which door you are at (and helps when several tabs are open).
  useEffect(() => {
    if (step === 'form') document.title = `Sign in · ${ROLE_CONFIGS[activeRole].pill}`;
    else if (step === 'choose') document.title = 'Sign in';
  }, [step, activeRole]);

  // Put the cursor in the username field, but not on touch screens where it would
  // pop the keyboard over the page before the visitor has even read it.
  useEffect(() => {
    if (step !== 'form') return;
    if (window.matchMedia?.('(pointer: fine)').matches) usernameRef.current?.focus();
  }, [step, activeRole]);

  const handleRoleChange = (role) => {
    setActiveRole(role);
    rememberRole(role);
    setError(null);
    setNotice(null);
    setUsername('');
    setPassword('');
    setIsPasswordVisible(false);
    setCapsLockOn(false);
    router.replace(`/login?role=${role}`, { scroll: false });
  };

  const chooseRole = (role) => {
    handleRoleChange(role);
    setStep('form');
  };

  const backToChooser = () => {
    setError(null);
    setStep('choose');
    router.replace('/login', { scroll: false });
  };

  // Arrow keys move between the role tabs, like any native tab control.
  const handleTabKeyDown = (e) => {
    const roles = Object.keys(ROLE_CONFIGS);
    const i = roles.indexOf(activeRole);
    let next = null;
    if (e.key === 'ArrowRight') next = roles[(i + 1) % roles.length];
    else if (e.key === 'ArrowLeft') next = roles[(i - 1 + roles.length) % roles.length];
    else if (e.key === 'Home') next = roles[0];
    else if (e.key === 'End') next = roles[roles.length - 1];
    if (!next) return;
    e.preventDefault();
    handleRoleChange(next);
    document.getElementById(`role-tab-${next}`)?.focus();
  };

  const trackCapsLock = (e) => setCapsLockOn(Boolean(e.getModifierState?.('CapsLock')));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmittingInfo(true);
    setError(null);

    if (!username.trim() || !password) {
      setError(!username.trim() ? 'Please enter your username.' : 'Please enter your password.');
      setSubmittingInfo(false);
      // Land the cursor on whatever is missing instead of making them hunt for it.
      (!username.trim() ? usernameRef : passwordRef).current?.focus();
      return;
    }

    const config = ROLE_CONFIGS[activeRole];

    try {
      const response = await axios.post(`${API_URL}${config.apiPath}`, {
        // Phones and copy-paste often leave a trailing space, which would fail an otherwise valid login.
        username: username.trim(),
        password,
        remember: rememberMe,
      }, { withCredentials: true });

      clearLocalSession(dispatch);

      const role = response.data?.role || config.storageRole;
      dispatch(config.dispatchAction(response.data));
      localStorage.setItem('userRole', role);
      rememberRole(activeRole);

      window.location.href = '/dashboard';
    } catch (err) {
      const message =
        err.response?.data?.message ||
        'Login failed. Please check your credentials and try again.';
      setError(message);
      console.error(`${activeRole} login error:`, err);
    }

    setSubmittingInfo(false);
  };

  // Google Sign-In response handler
  const handleGoogleCredentialResponse = async (response) => {
    if (!response.credential) return;
    setGoogleLoading(true);
    setError(null);

    try {
      const res = await axios.post(`${API_URL}/public/api/auth/google`, {
        credential: response.credential
      }, { withCredentials: true });

      if (res.data?.success) {
        if (res.data.isNewUser) {
          // Brand new organic user -> trigger Onboarding Modal
          setGoogleProfile(res.data.profile);
          setOnboardingToken(res.data.onboardingToken);
          setOnboardingModal(true);
        } else {
          // Existing user logged in
          clearLocalSession(dispatch);
          const role = res.data.role;
          localStorage.setItem('userRole', role);
          if (role === 'admin' && res.data.school) {
            dispatch(updateSchool(res.data.school));
          } else if (role === 'teacher' && res.data.teacher) {
            dispatch(setTeacher({ teacher: { ...res.data.teacher, schoolInfo: res.data.school } }));
          } else if (role === 'student' && res.data.student) {
            dispatch(setStudentAuth({ student: { ...res.data.student, schoolInfo: res.data.school } }));
          }
          window.location.href = res.data.redirectUrl || '/dashboard';
        }
      }
    } catch (err) {
      console.error('Google login error:', err);
      setError(err.response?.data?.message || 'Google sign-in failed. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  // Initialize Google Identity Services
  useEffect(() => {
    // Only show Google Login on platform default domain (hide on vendor white-labeled portals)
    // The button lives inside the sign-in form, which only exists after the role step.
    if (!googleEnabled || step !== 'form') return;

    const clientId = GOOGLE_CLIENT_ID;

    window.onGoogleLibraryLoad = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true
        });

        const btnElement = document.getElementById('google-btn-container');
        if (btnElement) {
          // GSI only accepts a pixel width (100-400); '100%' is rejected with a console error.
          const containerWidth = Math.round(btnElement.getBoundingClientRect().width) || 320;
          window.google.accounts.id.renderButton(btnElement, {
            theme: 'outline',
            size: 'large',
            width: Math.min(400, Math.max(200, containerWidth)),
            text: 'continue_with',
            shape: 'rectangular',
            logo_alignment: 'left'
          });
        }
      }
    };

    if (window.google?.accounts?.id) {
      window.onGoogleLibraryLoad();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleEnabled, step]);

  // Escape closes the onboarding dialog, like every other dialog in the app.
  useEffect(() => {
    if (!onboardingModal) return;
    const onKeyDown = (e) => { if (e.key === 'Escape') setOnboardingModal(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onboardingModal]);

  // Submit Organic Onboarding Flow
  const handleOnboardSubmit = async (e) => {
    e.preventDefault();
    setOnboardSubmitting(true);
    setError(null);

    try {
      let payload = {};
      if (onboardingIntent === 'create_school') {
        payload = {
          schoolName: onboardForm.schoolName,
          schoolAlias: onboardForm.schoolAlias,
          contactNo: onboardForm.contactNo,
          address: onboardForm.address
        };
      } else if (onboardingIntent === 'link_student') {
        payload = {
          username: onboardForm.studentUsername,
          password: onboardForm.studentPassword
        };
      } else if (onboardingIntent === 'vendor_partner') {
        payload = {
          mobilenumber: onboardForm.mobilenumber,
          organizationName: onboardForm.organizationName
        };
      }

      const res = await axios.post(`${API_URL}/public/api/auth/google/onboard`, {
        onboardingToken,
        intent: onboardingIntent,
        payload
      }, { withCredentials: true });

      if (res.data?.success) {
        if (res.data.role === 'admin' && res.data.school) {
          clearLocalSession(dispatch);
          dispatch(updateSchool(res.data.school));
          localStorage.setItem('userRole', 'admin');
          window.location.href = res.data.redirectUrl || '/dashboard';
        } else if (res.data.role === 'student' && res.data.student) {
          clearLocalSession(dispatch);
          dispatch(setStudentAuth({ student: { ...res.data.student, schoolInfo: res.data.school } }));
          localStorage.setItem('userRole', 'student');
          window.location.href = res.data.redirectUrl || '/dashboard';
        } else {
          setOnboardingModal(false);
          setNotice(res.data.message || 'Onboarding complete!');
        }
      }
    } catch (err) {
      console.error('Onboarding submit error:', err);
      setError(err.response?.data?.message || 'Failed to complete onboarding');
    } finally {
      setOnboardSubmitting(false);
    }
  };

  const activeConfig = ROLE_CONFIGS[activeRole];

  return (
    <div className={`role-login role-${activeRole}`}>
      {/* Load Google Identity Services script for organic domain */}
      {googleEnabled && (
        <Script
          src="https://accounts.google.com/gsi/client"
          strategy="afterInteractive"
          onLoad={() => {
            if (typeof window.onGoogleLibraryLoad === 'function') {
              window.onGoogleLibraryLoad();
            }
          }}
        />
      )}

      {/* Step 1 - who is signing in? Shown to first-time visitors only. */}
      {step === 'choose' && (
        <div className="role-chooser" role="region" aria-labelledby="chooser-title">
          <div className="chooser-brand">
            {tenant.logoUrl && tenant.logoUrl !== '/images/logo.png' ? (
              <img src={tenant.logoUrl} alt={tenant.brandName} style={{ height: 52, maxWidth: 180, objectFit: 'contain' }} />
            ) : (
              <Image width={56} height={56} src={logo} alt="Logo" priority />
            )}
          </div>
          <h1 id="chooser-title">
            {tenant.isPlatformDefault ? 'Welcome! Who is signing in?' : `Welcome to ${tenant.brandName}`}
          </h1>
          <p className="chooser-lead">Pick your role and we will take you to the right sign-in.</p>

          {notice && <p className="notice" role="status">{notice}</p>}

          <div className="chooser-cards">
            {ROLE_CHOICES.map(({ role, icon, label, hint }) => (
              <button
                key={role}
                type="button"
                className={`chooser-card card-${role}`}
                onClick={() => chooseRole(role)}
              >
                <span className="chooser-icon" aria-hidden="true"><FontAwesomeIcon icon={icon} fixedWidth /></span>
                <span className="chooser-text">
                  <strong>{label}</strong>
                  <small>{hint}</small>
                </span>
                <FontAwesomeIcon icon={faChevronRight} className="chooser-arrow" aria-hidden="true" />
              </button>
            ))}
          </div>

          {tenant.isPlatformDefault && (
            <p className="chooser-foot">
              Reselling VidyalayHelp? <Link href="/vendor/login">Partner sign in</Link>
            </p>
          )}
        </div>
      )}

      {/* Step 2 - the sign-in form. The tab switcher stays so a wrong pick is one click to fix. */}
      {step === 'form' && (
      <div className="role-login-shell">
        <div className="role-left">
          <button type="button" className="chooser-back" onClick={backToChooser}>
            <FontAwesomeIcon icon={faArrowLeft} fixedWidth /> Not sure? See all options
          </button>

          {/* Unified switcher tab control */}
          <div className="role-switcher" role="tablist" aria-label="Sign in as" onKeyDown={handleTabKeyDown}>
            {ROLE_CHOICES.map(({ role, tab }) => (
              <button
                key={role}
                id={`role-tab-${role}`}
                type="button"
                role="tab"
                aria-selected={activeRole === role}
                tabIndex={activeRole === role ? 0 : -1}
                className={`role-tab ${activeRole === role ? 'active' : ''}`}
                onClick={() => handleRoleChange(role)}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="brand-row">
            {tenant.logoUrl && tenant.logoUrl !== '/images/logo.png' ? (
              <img src={tenant.logoUrl} alt={tenant.brandName} style={{ height: 48, maxWidth: 160, objectFit: 'contain' }} />
            ) : (
              <Image width={54} height={54} src={logo} alt="Logo" priority />
            )}
            <span className="role-pill">{activeConfig.pill}</span>
          </div>

          <h2>{tenant.isPlatformDefault ? activeConfig.title : `Welcome to ${tenant.brandName}`}</h2>
          <p className="lead">{tenant.isPlatformDefault ? activeConfig.lead : `Sign in to access your ${activeConfig.pill.toLowerCase()} portal.`}</p>

          {notice && (
            <p className="notice" role="status">
              {notice}
            </p>
          )}

          {/* Organic Google Sign-In Option (Only on platform domain) */}
          {googleEnabled && (
            <div className="google-auth-wrapper">
              <div id="google-btn-container" style={{ minHeight: '44px' }}></div>
              <div className="auth-divider">
                <span>or continue with password</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                name="username"
                ref={usernameRef}
                placeholder={`Enter your ${activeRole} username`}
                value={username}
                onChange={(e) => { setUsername(e.target.value); if (error) setError(null); }}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? 'login-error' : undefined}
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
            </div>

            <div className="input-group">
              <label htmlFor="password">Password</label>
              <div className="password-wrapper">
                <input
                  type={isPasswordVisible ? 'text' : 'password'}
                  id="password"
                  name="password"
                  ref={passwordRef}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); if (error) setError(null); }}
                  onKeyDown={trackCapsLock}
                  onKeyUp={trackCapsLock}
                  onBlur={() => setCapsLockOn(false)}
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? 'login-error' : capsLockOn ? 'caps-warning' : undefined}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setIsPasswordVisible((v) => !v)}
                  aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
                >
                  {<FontAwesomeIcon icon={isPasswordVisible ? faEyeSlash : faEye} fixedWidth />}
                </button>
              </div>
              {capsLockOn && (
                <p id="caps-warning" className="caps-warning" role="status">Caps Lock is on - passwords are case-sensitive.</p>
              )}
            </div>

            <div className="form-row">
              <label className="remember-me">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span>Keep me signed in for 30 days</span>
              </label>
            </div>

            <button aria-busy={submittingInfo} type="submit" className="role-submit" disabled={submittingInfo || googleLoading}>
              {submittingInfo ? 'Signing in...' : 'Sign In'}
            </button>

            {error && <p id="login-error" className="error" role="alert">{error}</p>}
          </form>

          <p className="login-help">
            Forgot your password? Ask {activeRole === 'admin' ? 'your VidyalayHelp partner or support team' : 'your school office'} to reset it for you.
            {tenant.isPlatformDefault && (
              <>
                {' '}Reselling VidyalayHelp?{' '}
                <Link href="/vendor/login">Partner sign in</Link>
              </>
            )}
          </p>

          {/* Footer branding */}
          {tenant.footerCopyright && (
            <p className="tenant-login-footer">
              {tenant.footerCopyright}
            </p>
          )}
        </div>

        <div className="role-right">
          <h3>{tenant.isPlatformDefault ? activeConfig.highlightTitle : `${tenant.brandName} Portal`}</h3>
          <p>{tenant.isPlatformDefault ? activeConfig.highlightLead : 'Fast, reliable, and secure school management workspace.'}</p>
          <ul>
            {activeConfig.highlights.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </div>
      </div>
      )}

      {/* Organic Google User Onboarding Modal (macOS Design) */}
      {onboardingModal && (
        <div className="modal-overlay" onClick={() => setOnboardingModal(false)}>
          <div className="modal-box organic-onboarding-modal" role="dialog" aria-modal="true" aria-label="Finish setting up your account" onClick={e => e.stopPropagation()}>
            <div className="modal-macos-header">
              <div className="macos-traffic-dots">
                <span className="dot red" onClick={() => setOnboardingModal(false)}></span>
                <span className="dot yellow"></span>
                <span className="dot green"></span>
              </div>
              <h3>Welcome to VidyalayHelp!</h3>
              <button type="button" aria-label="Close dialog" className="modal-close-btn" onClick={() => setOnboardingModal(false)}><FontAwesomeIcon icon={faXmark} fixedWidth /></button>
            </div>

            <div className="user-profile-preview">
              {googleProfile?.picture && (
                <img src={googleProfile.picture} alt={googleProfile.name} className="google-avatar" />
              )}
              <div>
                <strong>{googleProfile?.name}</strong>
                <p>{googleProfile?.email}</p>
              </div>
            </div>

            <p className="onboarding-prompt">
              Tell us how you plan to use the platform so we can configure your workspace:
            </p>

            <div className="intent-tabs">
              <button 
                type="button" 
                className={`intent-tab ${onboardingIntent === 'create_school' ? 'active' : ''}`}
                onClick={() => setOnboardingIntent('create_school')}
              >
                <FontAwesomeIcon icon={faSchool} fixedWidth /> School Owner / Principal
              </button>
              <button 
                type="button" 
                className={`intent-tab ${onboardingIntent === 'link_student' ? 'active' : ''}`}
                onClick={() => setOnboardingIntent('link_student')}
              >
                <FontAwesomeIcon icon={faUserGraduate} fixedWidth /> Student / Parent
              </button>
              <button 
                type="button" 
                className={`intent-tab ${onboardingIntent === 'vendor_partner' ? 'active' : ''}`}
                onClick={() => setOnboardingIntent('vendor_partner')}
              >
                <FontAwesomeIcon icon={faHandshake} fixedWidth /> Reseller Partner
              </button>
            </div>

            <form onSubmit={handleOnboardSubmit} className="onboarding-form">
              {onboardingIntent === 'create_school' && (
                <>
                  <div className="form-group">
                    <label>School Name</label>
                    <input 
                      type="text" 
                      placeholder="e.g. St. Xavier High School"
                      value={onboardForm.schoolName}
                      onChange={e => setOnboardForm({ ...onboardForm, schoolName: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>School Short Alias</label>
                    <input 
                      type="text" 
                      placeholder="e.g. stxavier"
                      value={onboardForm.schoolAlias}
                      onChange={e => setOnboardForm({ ...onboardForm, schoolAlias: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Official Contact Number</label>
                    <input 
                      type="text" 
                      placeholder="e.g. 9876543210"
                      value={onboardForm.contactNo}
                      onChange={e => setOnboardForm({ ...onboardForm, contactNo: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>School Address / City</label>
                    <input 
                      type="text" 
                      placeholder="e.g. New Delhi, India"
                      value={onboardForm.address}
                      onChange={e => setOnboardForm({ ...onboardForm, address: e.target.value })}
                    />
                  </div>
                </>
              )}

              {onboardingIntent === 'link_student' && (
                <>
                  <p className="intent-instruction">
                    Link your student record to enable instant Google login in the future:
                  </p>
                  <div className="form-group">
                    <label>Student Username</label>
                    <input 
                      type="text" 
                      placeholder="e.g. rahul_102"
                      value={onboardForm.studentUsername}
                      onChange={e => setOnboardForm({ ...onboardForm, studentUsername: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Current Student Password</label>
                    <input 
                      type="password" 
                      placeholder="Enter student password"
                      value={onboardForm.studentPassword}
                      onChange={e => setOnboardForm({ ...onboardForm, studentPassword: e.target.value })}
                      required
                    />
                  </div>
                </>
              )}

              {onboardingIntent === 'vendor_partner' && (
                <>
                  <p className="intent-instruction">
                    Join our vendor partner network to deploy white-labeled portals to schools:
                  </p>
                  <div className="form-group">
                    <label>Mobile Number</label>
                    <input 
                      type="text" 
                      placeholder="10-digit mobile number"
                      value={onboardForm.mobilenumber}
                      onChange={e => setOnboardForm({ ...onboardForm, mobilenumber: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Company / Agency Name</label>
                    <input 
                      type="text" 
                      placeholder="Your organization name"
                      value={onboardForm.organizationName}
                      onChange={e => setOnboardForm({ ...onboardForm, organizationName: e.target.value })}
                    />
                  </div>
                </>
              )}

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setOnboardingModal(false)}>
                  Cancel
                </button>
                <button aria-busy={onboardSubmitting} type="submit" className="btn-onboard-submit" disabled={onboardSubmitting}>
                  {onboardSubmitting ? 'Configuring...' : 'Finish & Launch'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Suspense Loader Component
function LoginLoader() {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: '#f8fafc',
      fontFamily: 'system-ui, sans-serif',
      textAlign: 'center'
    }}>
      <div>
        <div style={{
          width: '50px',
          height: '50px',
          border: '3px solid #e2e8f0',
          borderTop: '3px solid #3b82f6',
          borderRadius: '50%',
          margin: '0 auto 1.5rem',
          animation: 'spin 1s linear infinite'
        }} />
        <h2 style={{ color: '#1e293b', fontSize: '1.25rem', margin: '0' }}>Loading Portal...</h2>
        <style jsx>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<LoginLoader />}>
      <LoginContent />
    </Suspense>
  );
}
